<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <page-header
      :title="$t('grocery_list')"
      :subtitle="$t('manage_ingredients')"
      icon="mdi-food-apple"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <primary-button
          v-if="$store.state.restaurant.parent"
          class="mr-2"
          @click="cloneGroceryDialog = true"
        >
          {{ $t("clone") }}
          {{ $store.state.restaurant.parent ? $store.state.restaurant.parent.name : "" }}
        </primary-button>

        <global-button-print @click="printGroceries" />
        <global-button-excel @click="exportGroceries" />

        <primary-button @click="editGroceryDialog = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_grocery") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar
      v-if="!device.isMobile"
      :search.sync="searchGrocery"
      :search-placeholder="$t('search_ingredients_placeholder')"
      searchable
      :loading="isLoading"
    />

    <grocery-dialog
      v-model="editGroceryDialog"
      :edited-item="editedGrocery"
      @groceryCreated="addGrocery"
      @groceryUpdated="updateGrocery"
    />

    <global-dialog-card
      v-model="cloneGroceryDialog"
      :title="$t('clone')"
      :subtitle="$store.state.restaurant.parent?.name || ''"
    >
      <text-field
        v-model="searchParentGrocery"
        class="mt-2"
        append-icon="mdi-magnify"
        :label="$t('search')"
        hide-details
      ></text-field>
      <v-data-table
        dense
        :headers="headersN"
        class="ordera-box-shadow striped-table"
        :search="searchParentGrocery"
        style="height: 100%"
        :items="parentGroceries"
      >
        <template
          v-for="header in headersN"
          v-slot:[`header.${header.value}`]="{ header }"
        >
          <span v-if="header.mark">
            <input
              id="checkbox_id"
              type="checkbox"
              v-model="selectAllGroceries"
              @change.prevent="selectAll()"
            />
            <label for="checkbox_id">{{ $t("mark") }}</label>
          </span>
          <span v-else>{{ $t(header.text) }}</span>
        </template>
        <template v-slot:item.actions="{ item, index }">
          <span v-if="!prepData[item.id]">
            <input type="checkbox" v-model="item.selected" />
          </span>
          <span v-else style="font-size: 13px; color: rgba(0, 0, 0, 0.4)">{{
            $t("cloned")
          }}</span>
        </template>
      </v-data-table>
      <template #actions>
        <v-btn text @click="cloneGroceryDialog = false">
          {{ $t("cancel") }}
        </v-btn>
        <primary-button @click="saveClone">
          {{ $t("clone") }}
        </primary-button>
      </template>
    </global-dialog-card>

    <!-- Desktop View -->
    <template v-if="!device.isMobile">
      <div class="px-6 mt-2">
        <v-data-table
          :headers="headers"
          :items="customSearch(groceries, headers, searchGrocery)"
          class="striped-table ordera-box-shadow"
          :loading="isLoading"
          :loading-text="$t('loading_data')"
          show-expand
        >
          <template v-slot:item.images="{ item }">
            <v-img
              v-if="item.images && item.images.length"
              :src="groceryImageUrl(item.images[0])"
              width="56"
              contain
              style="border-radius: 4px; background-color: #f5f5f5;"
            >
              <template #placeholder>
                <div style="width: 56px; height: 56px; display: flex; align-items: center; justify-content: center; background-color: #f5f5f5; border-radius: 4px;">
                  <v-icon small color="grey lighten-1">mdi-food-apple-outline</v-icon>
                </div>
              </template>
            </v-img>
            <div v-else  style="width: 56px; height: 56px; display: flex; align-items: center; justify-content: center; background-color: #f5f5f5; border-radius: 4px;">
              <v-icon small color="grey lighten-1">mdi-food-apple-outline</v-icon>
            </div>
          </template>
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <template v-if="item.children && item.children.length">
              <circle-button
                @click="expand(!isExpanded)"
                :icon="isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-up'"
              ></circle-button>
            </template>
          </template>
          <template v-slot:expanded-item="{ item, isExpanded, expand }">
            <template v-if="item.children">
              <td :colspan="headers.length" class="px-0">
                <v-data-table
                  dense
                  :headers="headers"
                  class="ordera-box-shadow striped-table"
                  :items="item.children || []"
                  hide-default-footer
                  hide-default-header
                >
                  <template v-slot:item.actions="{ item, index }">
                    <edit-button class="mr-2" @click="editItem(item)"></edit-button>
                    <remove-button @click="deleteItem(item)"></remove-button>
                  </template>
                </v-data-table>
              </td>
            </template>
          </template>
          <template v-slot:item.actions="{ item }">
            <edit-button class="mr-2" @click="editItem(item)"></edit-button>
            <remove-button @click="deleteItem(item)"></remove-button>
          </template>
        </v-data-table>
      </div>
    </template>

    <!-- Mobile View -->
    <template v-else>
      <div class="px-3 mt-4">
        <mobile-data-cards
          :headers="mobileHeaders"
          :items="customSearch(groceries, headers, searchGrocery)"
          :loading="isLoading"
          item-key="id"
          show-filters
          :show-row-number="false"
          :fields-per-row="4"
          :bold-fields="['name']"
        >
          <template v-slot:item.actions="{ item }">
            <edit-button class="mr-2" @click="editItem(item)"></edit-button>
            <remove-button @click="deleteItem(item)"></remove-button>
          </template>
        </mobile-data-cards>
      </div>

      <div class="fixed-bottom-button">
        <v-btn color="primary" block @click="editGroceryDialog = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_grocery") }}
        </v-btn>
      </div>
    </template>

    <!-- Mobile FAB Menu -->
    <v-speed-dial
      v-if="device.isMobile"
      v-model="fabMenu"
      fixed
      right
      direction="bottom"
      transition="slide-y-reverse-transition"
      style="top: 80px"
    >
      <template v-slot:activator>
        <v-btn v-model="fabMenu" color="grey" fab dark small depressed>
          <v-icon v-if="fabMenu">mdi-close</v-icon>
          <v-icon v-else>mdi-dots-vertical</v-icon>
        </v-btn>
      </template>

      <!-- Excel Button -->
      <v-btn fab small color="green darken-2" dark @click="exportGroceries">
        <v-icon>mdi-microsoft-excel</v-icon>
      </v-btn>

      <!-- Print Button -->
      <v-btn fab small color="blue-grey" dark @click="printGroceries">
        <v-icon>mdi-printer</v-icon>
      </v-btn>

      <!-- Refresh Button -->
      <v-btn fab small color="blue" dark @click="initialize">
        <v-icon>mdi-reload</v-icon>
      </v-btn>
    </v-speed-dial>
  </div>
</template>

<script>
import { defaultGrocery } from "@/models/food";
import GroceryDialog from "@/components/groceries/dialogs/GroceryDialog.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import GlobalButtonExcel from "@/components/global/buttons/GlobalButtonExcel.vue";
import GlobalButtonPrint from "@/components/global/buttons/GlobalButtonPrint.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import groceryService from "@/services/grocery";
import conf from "@/config.json";

export default {
  mixins: [hasSnackbar, hasPrinting, hasExcel],
  components: {
    GroceryDialog,
    PageHeader,
    PageToolbar,
    MobileDataCards,
    GlobalButtonExcel,
    GlobalButtonPrint,
  },
  data: () => ({
    fabMenu: false,
    editGroceryDialog: false,
    cloneGroceryDialog: false,
    searchGrocery: "",
    searchParentGrocery: "",
    selectAllGroceries: false,
    groceries: [],
    editedGrocery: defaultGrocery,
    parentGroceries: [],
    isLoading: false,
  }),
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    headers() {
      return [
        { text: this.$t("id"), align: "start", value: "id" },
        { text: 'Slika', align: 'start', value: 'images', sortable: false },
        { text: this.$t("code"), align: "start", value: "code" },
        { text: this.$t("title"), align: "start", value: "name" },
        {
          text: this.$t("q"),
          align: "start",
          value: "measure",
          sortable: false,
        },
        {
          text: this.$t("actions"),
          align: "right",
          value: "actions",
          sortable: false,
        },
        { text: "", value: "data-table-expand" },
      ];
    },
    headersN() {
      return [
        {
          text: this.$t("mark"),
          align: "center",
          value: "actions",
          sortable: false,
          mark: true,
        },
        { text: this.$t("title"), align: "start", value: "name" },
        {
          text: this.$t("q"),
          align: "start",
          value: "measure",
          sortable: false,
        },
      ];
    },
    prepData() {
      const clonedIds = {};
      this.groceries.forEach((item) => {
        if (item.clone_grocery_id) {
          clonedIds[item.clone_grocery_id] = true;
        }
      });
      return clonedIds;
    },
    mobileHeaders() {
      return [
        { text: this.$t("code"), value: "code", cols: 3 },
        { text: this.$t("title"), value: "name", cols: 4 },
        { text: this.$t("q"), value: "measure", cols: 2 },
        { text: this.$t("actions"), value: "actions", cols: 3 },
      ];
    },
  },
  watch: {
    editGroceryDialog(val) {
      if (!val) {
        this.$nextTick(() => {
          this.editedGrocery = { ...defaultGrocery };
        });
      }
    },
  },
  created() {
    if (this.$store.state.restaurant.parent_id) {
      this.getParentGroceries();
    }

    this.initialize();
  },
  methods: {
    groceryImageUrl(image) {
      const isSupplier = this.user?.type === 'supplier';
      const entityId = isSupplier
        ? this.user?.suppliers?.[0]?.id
        : this.user?.restaurants?.[0]?.id;
      const entityPath = isSupplier ? 'suppliers' : 'restaurants';
      return `${conf.apiUrlBase}/${entityPath}/${entityId}/small/${image.image_path}`;
    },
    saveClone() {
      const restaurantId = this.$store.state.restaurant.id;
      const selectedItems = this.parentGroceries
        .filter((item) => item.selected)
        .map((item) => ({ ...item, restoran: restaurantId }));

      if (!selectedItems.length) return;

      groceryService
        .cloneParentGroceries({ items: selectedItems, restoran: restaurantId })
        .then(({ data }) => {
          if (data) {
            this.initialize();
            this.cloneGroceryDialog = false;
            this.searchParentGrocery = "";
          }
        });
    },
    selectAll() {
      this.parentGroceries.forEach((item) => {
        item.selected = this.selectAllGroceries;
      });
    },
    getParentGroceries() {
      groceryService
        .getParentGroceries(this.$store.state.restaurant.parent_id)
        .then(({ data }) => {
          this.parentGroceries = data.map((item) => ({ ...item, selected: false }));
        });
    },
    async initialize() {
      this.isLoading = true;
      try {
        const res = await groceryService.getGroceries();
        this.groceries = res.data;
      } finally {
        this.isLoading = false;
      }
    },
    editItem(item) {
      this.editedGrocery = Object.assign({}, item);
      this.editGroceryDialog = true;
    },
    deleteItem(item) {
      this.$refs.confirmBox
        .open(this.$t("delete_confirmation"), this.$t("delete_grocery_text"), {
          color: "error",
        })
        .then(() => {
          groceryService.deleteGrocery(item.id).then(() => {
            if (item.parent_id) {
              const parent = this.groceries.find((i) => i.id === item.parent_id);
              if (parent?.children) {
                const idx = parent.children.findIndex((i) => i.id === item.id);
                if (idx !== -1) parent.children.splice(idx, 1);
              }
            } else {
              const idx = this.groceries.findIndex((i) => i.id === item.id);
              if (idx !== -1) this.groceries.splice(idx, 1);
            }
            this.showSuccessSnackbar(this.$t("ingredient_deleted"));
          });
        })
        .catch((err) => console.warn(err));
    },
    addGrocery(grocery) {
      const groceryToAdd = { ...this.editedGrocery, id: grocery.id };
      if (this.editedGrocery.parent_id) {
        const parent = this.groceries.find((i) => i.id === this.editedGrocery.parent_id);
        if (parent?.children) parent.children.push(groceryToAdd);
      } else {
        this.groceries.push(groceryToAdd);
      }
      this.editGroceryDialog = false;
    },
    updateGrocery() {
      if (this.editedGrocery.parent_id) {
        const parent = this.groceries.find((i) => i.id === this.editedGrocery.parent_id);
        if (parent?.children) {
          const idx = parent.children.findIndex((i) => i.id === this.editedGrocery.id);
          if (idx !== -1) Object.assign(parent.children[idx], this.editedGrocery);
        }
      } else {
        const idx = this.groceries.findIndex((i) => i.id === this.editedGrocery.id);
        if (idx !== -1) Object.assign(this.groceries[idx], this.editedGrocery);
      }
      this.editGroceryDialog = false;
    },
    getExportData() {
      return {
        headers: [
          { text: this.$t("id"), value: "id", print: true },
          { text: this.$t("code"), value: "code", print: true },
          { text: this.$t("title"), value: "name", print: true },
          { text: this.$t("q"), value: "measure", print: true },
        ],
        items: this.customSearch(this.groceries, this.headers, this.searchGrocery),
      };
    },
    printGroceries() {
      this.setPrinter();
      const { headers, items } = this.getExportData();
      this.printDocument({
        pageTitle: this.$t("grocery_list"),
        title: this.$t("grocery_list"),
        subtitle: this.$t("manage_ingredients"),
        tables: [{ headers, items }],
      });
    },
    exportGroceries() {
      this.setExcel();
      const { headers, items } = this.getExportData();
      this.downloadExcel({
        fileName: `namirnice_${new Date().toISOString().substr(0, 10)}.xlsx`,
        sheets: [
          {
            name: this.$t("grocery_list"),
            title: this.$t("grocery_list"),
            subtitle: this.$t("manage_ingredients"),
            headers,
            items,
          },
        ],
      });
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
</style>
